import { ReactNode, ReactElement, useState, useCallback } from "react";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import StyledModalFooter from "../StyledModalFooter/StyledModalFooter";
import { useTranslation } from "@coworker/locales";
import styles from "./TitledBoxedTextWithModal.module.css";

interface TitledBoxedTextWithModalProps {
  /** Section title (pre-translated) */
  title: string;
  /** Optional section subtitle (pre-translated) */
  subTitle?: string;

  /** List item title (pre-translated or formatted, e.g., "14 pieces") */
  listItemTitle: string;
  /** Optional CSS class for list item */
  listItemClassName?: string;
  /** Test ID for list item */
  testId?: string;
  /** Disable list item interaction */
  disabled?: boolean;

  /** Modal title (pre-translated) */
  modalTitle: string;
  /** Modal body content */
  modalContent: ReactNode;
  /** Optional custom modal footer. If not provided, shows default "Save" button */
  modalFooter?: ReactElement | null;
  /** Optional callback when modal opens */
  onModalOpen?: () => void;
  /** Optional callback when modal is saved/closed */
  onSave?: () => void;
}

export const TitledBoxedTextWithModal = ({
  title,
  subTitle,
  listItemTitle,
  listItemClassName,
  testId,
  disabled,
  modalTitle,
  modalContent,
  modalFooter,
  onModalOpen,
  onSave,
}: TitledBoxedTextWithModalProps) => {
  const { t } = useTranslation();
  const headerId = `titled-section-modal-header-${testId || "default"}`;

  const [showModal, setShowModal] = useState(false);

  const handleOpenModal = useCallback(() => {
    if (onModalOpen) {
      onModalOpen();
    }
    setShowModal(true);
  }, [onModalOpen]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave();
    }
    handleCloseModal();
  }, [onSave, handleCloseModal]);

  const defaultFooter = (
    <StyledModalFooter>
      <FixaButton text={t("saveString")} type="primary" onClick={handleSave} />
    </StyledModalFooter>
  );

  return (
    <div>
      {/* Title section */}
      <div className={styles["titleSection"]}>
        <FixaText textStyle="Heading.S">{title}</FixaText>
        {subTitle && <FixaText textStyle="Body.L">{subTitle}</FixaText>}
      </div>

      {/* List item */}
      <FixaListView
        id={`${testId || "titled-section"}-list`}
        showDivider={false}
        size="small"
      >
        <FixaListViewItem
          inset
          className={listItemClassName ?? styles["listViewItemBackground"]}
          data-testid={testId}
          title={listItemTitle}
          control="navigational"
          controlIcon="chevronRight"
          disabled={disabled}
          type="button"
          onClick={handleOpenModal}
          emphasised={true}
          showDivider={false}
        />
      </FixaListView>

      {/* Modal */}
      <FixaModal visible={showModal} handleCloseBtn={handleCloseModal}>
        <FixaSheets
          labelledById={headerId}
          header={
            <FixaModalHeader
              id={headerId}
              title={modalTitle}
              closeBtnClick={handleCloseModal}
            />
          }
          footer={modalFooter ?? defaultFooter}
          fullSize
        >
          <FixaModalBody>{modalContent}</FixaModalBody>
        </FixaSheets>
      </FixaModal>
    </div>
  );
};
